import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

export default function Terms() {
  return (
    <div data-testid="terms-page" className="max-w-3xl mx-auto px-6 py-16 md:py-24 text-cocoa/80 leading-relaxed">
      <Link to="/"><Logo size="md" /></Link>
      <div className="mt-10">
        <div className="text-[10px] tracking-[0.4em] uppercase text-mauve">Legal</div>
        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight mt-3 text-cocoa">Terms of Use</h1>
        <p className="text-[10px] tracking-[0.3em] uppercase text-cocoa/50 mt-3">Last updated: this build · Governed by New South Wales, Australia</p>
      </div>

      <section className="mt-10 space-y-4 text-sm">
        <h2 className="font-heading text-xl text-cocoa">1. What SugarSense is, and is not</h2>
        <p>
          SugarSense is a private behavioural-intelligence platform for people navigating
          emotionally complex, high-stakes, or hard-to-read relationships. Its purpose is
          to help a user interpret patterns of behaviour they have already observed, name
          the archetype that fits, and choose a calm next move. SugarSense is{" "}
          <span className="text-mauve font-medium">not</span> a dating site, an escort service, a
          matchmaking service, a payment processor, a counsellor, a therapist, or a
          financial or legal advisor. We do not introduce parties, broker introductions,
          rate or rank people, or transmit funds between users.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">2. Eligibility</h2>
        <p>
          You must be at least 18 years old (or the age of majority in your jurisdiction —
          whichever is higher) to register. By creating an account you confirm you meet
          this requirement.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">3. What you can do here</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>Describe a situation in your own words and receive an AI-generated
              interpretation, an archetype read, and a drafted next move.</li>
          <li>Keep a private, alias-tracked journal of relationship patterns and the
              advisor's reads of each one.</li>
          <li>Read editorial essays and archetype write-ups drawn from a deep, practical
              understanding of complex relationship dynamics, continually refined through
              structured feedback from the SugarSense community.</li>
          <li>Track, over time, whether a dynamic is shifting or simply repeating.</li>
        </ul>
        <p className="mt-3">
          The platform is built around your privacy. Where you choose to record names or
          identifying details, you may use aliases. Where the AI surfaces references to
          prior conversations, those references are routed through an anonymisation
          pipeline before any AI model sees them.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">4. The AI advisor — what it is and is not</h2>
        <p>
          The AI advisor draws on a proprietary 41-archetype behavioural ontology, built
          from deep research into complex relationship dynamics and continually refined
          through structured feedback from real users, plus general language-model
          reasoning. It is
          an analytical tool, not a therapist, not a doctor, and not a friend. Its
          outputs are interpretive opinions, not professional advice. If you are in
          crisis, in danger, or experiencing significant mental-health distress, please
          contact a qualified professional or a local emergency service. SugarSense
          should not be relied on as a substitute for legal, medical, financial, or
          mental-health counsel.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">5. Membership & payments</h2>
        <p>
          Pricing is shown on the <Link to="/pricing" className="text-mauve underline">Membership page</Link>.
          Paid memberships expire when the paid period ends; SugarSense does not
          automatically renew or take additional payments without your active checkout.
          Lifetime tiers are one-time purchases. Refunds for lifetime tiers are at the
          platform's discretion within 14 days of purchase, provided no significant use
          has occurred. Square Inc. is the merchant of record for membership payments;
          their terms apply to payment processing. We do not store full card details on
          our infrastructure.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">6. Your content & ownership</h2>
        <p>
          You retain ownership of the content you write into SugarSense. By submitting
          content you grant SugarSense a non-exclusive, revocable licence to process it
          for the sole purpose of delivering the platform's features to you (e.g., the
          advisor read, the archetype match, your journal history). Anonymised, de-
          identified data may be used to improve our internal behavioural ontology;
          identifiable content is never shared, sold, or used for any other purpose. See
          the <Link to="/privacy" className="text-mauve underline">Privacy Statement</Link> for full detail.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">7. Jurisdiction</h2>
        <p>
          SugarSense is operated from and governed by the laws of New South Wales,
          Australia. The platform is offered worldwide, but you remain responsible for
          ensuring that your use of the platform is lawful in your own jurisdiction. We
          provide no legal advice.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">8. Acceptable use</h2>
        <p>
          SugarSense is a respectful, low-drama platform. The following are prohibited:
          using the platform to harass, threaten or coerce another person; recording or
          submitting third-party content in a way that would breach that person's
          privacy or local laws; attempting to extract or scrape the editorial library,
          the archetype ontology, or other users' content; attempting to circumvent
          rate-limits, paywalls, or platform security; using the platform as part of a
          campaign of litigation against a third party; using the AI advisor's outputs
          as evidence in proceedings without independent professional review.
        </p>
        <p className="mt-3">
          We reserve the right to suspend or terminate accounts that breach these rules,
          with or without notice.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">9. Liability</h2>
        <p>
          SugarSense is provided "as is" without warranty of any kind. The platform's
          aggregate liability to any user is limited to the fees paid in the 12 months
          prior to the claim. We are not liable for any indirect, consequential, or
          incidental damage including loss of relationship, loss of opportunity, lost
          profit, or emotional distress arising from use of the platform or reliance
          on any output of the AI advisor.
        </p>

        <h2 className="font-heading text-xl text-cocoa mt-8">10. Changes</h2>
        <p>
          We may update these terms from time to time. Material changes will be
          surfaced on login. Continued use after a material change constitutes
          acceptance of the updated terms.
        </p>
      </section>

      <div className="mt-16 text-center">
        <Link to="/" className="text-[11px] tracking-[0.3em] uppercase text-mauve hover:underline">← Back to SugarSense</Link>
      </div>
    </div>
  );
}
